/**
 * Protected Route Component
 * 
 * Wraps routes that require authentication.
 * Redirects unauthenticated users to the login page.
 * 
 * Requirements: 1.3, 1.7
 */

import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { VoiceRecipeLogo } from './VoiceRecipeLogo';

const isPreviewMode = import.meta.env.VITE_PREVIEW_MODE === 'true';

/**
 * Props for the ProtectedRoute component
 */
interface ProtectedRouteProps {
    /** Child components to render when user is authenticated */
    children: React.ReactNode;
}

/**
 * ProtectedRoute Component
 * 
 * Ensures that only authenticated users can access wrapped routes.
 * Shows branded loading state while checking authentication.
 * Redirects to login if user is not authenticated.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
    const { isAuthenticated, isLoading } = useAuth();
    const location = useLocation();

    // Show branded loading state while checking authentication (e.g. OAuth callback)
    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 3 }}>
                <VoiceRecipeLogo size="large" />
                <CircularProgress size={40} />
            </Box>
        );
    }

    if (!isAuthenticated) {
        if (isPreviewMode) {
            return <Navigate to="/" replace />;
        }
        return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
    }

    // Render protected content
    return <>{children}</>;
}
